import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import { useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';
import './Users.css';

const api = axios.create({ withCredentials: true });

function Users() {
  const { user: currentUser, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'teacher'
  });

  useEffect(() => {
    if (hasRole('admin')) {
      fetchUsers();
    }
  }, [hasRole]);

  const fetchUsers = async () => {
    try {
      const response = await api.get(`${API_URL}/auth/users`);
      setUsers(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Fel vid hämtning av användare:', error);
      setError('Kunde inte hämta användare');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update user
        await api.put(`${API_URL}/auth/users/${editingUser.id}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role
        });
        setSuccess('Användare uppdaterad');
      } else {
        // Create new user
        await api.post(`${API_URL}/auth/users`, formData);
        setSuccess('Användare skapad');
      }

      resetForm();
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Något gick fel');
    }
  };

  const handleEdit = (user) => {
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role
    });
    setEditingUser(user);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`${API_URL}/auth/users/${user.id}`, {
        isActive: !user.isActive
      });
      setSuccess(`Användare ${user.isActive ? 'inaktiverad' : 'aktiverad'}`);
      fetchUsers();
    } catch (error) {
      setError('Kunde inte ändra status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Är du säker på att du vill ta bort ${user.email}?`)) {
      return;
    }

    try {
      await api.delete(`${API_URL}/auth/users/${user.id}`);
      setSuccess('Användare borttagen');
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Kunde inte ta bort användare');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'teacher'
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <span className="role-badge role-admin">Admin</span>;
      case 'teacher':
        return <span className="role-badge role-teacher">Lärare</span>;
      case 'viewer':
        return <span className="role-badge role-viewer">Läsare</span>;
      default:
        return <span className="role-badge">{role}</span>;
    }
  };

  // Only admins can access this page
  if (!hasRole('admin')) {
    return (
      <div className="users-container">
        <div className="access-denied">
          <h2>Åtkomst nekad</h2>
          <p>Du måste vara admin för att hantera användare.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Laddar användare..." />;
  }

  return (
    <div className="users-container">
      <header className="users-header">
        <div>
          <h1>Användare</h1>
          <p>Hantera systemanvändare och roller</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Stäng' : '+ Ny användare'}
        </button>
      </header>

      <AlertMessage error={error} success={success} />

      {showForm && (
        <div className="user-form-container">
          <h2>{editingUser ? 'Redigera användare' : 'Skapa ny användare'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Förnamn</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Förnamn"
                />
              </div>
              <div className="form-group">
                <label>Efternamn</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Efternamn"
                />
              </div>
            </div>

            <div className="form-group">
              <label>E-post *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="E-postadress"
                required
                disabled={editingUser}
              />
            </div>

            {!editingUser && (
              <div className="form-group">
                <label>Lösenord *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Minst 8 tecken"
                  required
                  minLength={8}
                />
              </div>
            )}

            <div className="form-group">
              <label>Roll *</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="teacher">Lärare - Kan hantera kurser och bokningar</option>
                <option value="admin">Admin - Full åtkomst</option>
                <option value="viewer">Läsare - Endast läsrättigheter</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingUser ? 'Spara ändringar' : 'Skapa användare'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Namn</th>
              <th>E-post</th>
              <th>Roll</th>
              <th>Status</th>
              <th>Senaste inloggning</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.isActive ? 'inactive' : ''}>
                <td>
                  <strong>{user.firstName} {user.lastName}</strong>
                </td>
                <td>{user.email}</td>
                <td>{getRoleBadge(user.role)}</td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td>
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString('sv-SE')
                    : 'Aldrig'
                  }
                </td>
                <td className="actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(user)}
                  >
                    Redigera
                  </button>
                  <button
                    className={`btn btn-small ${user.isActive ? 'btn-warning' : 'btn-success'}`}
                    onClick={() => handleToggleActive(user)}
                    disabled={user.id === currentUser?.id}
                  >
                    {user.isActive ? 'Inaktivera' : 'Aktivera'}
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(user)}
                    disabled={user.id === currentUser?.id}
                  >
                    Ta bort
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Users;
